// src/hooks/useSEO.js
//
// Client-side SEO metadata manager for our CRA marketing site.
//
// Note on scope: because this runs in `useEffect`, everything it sets is
// invisible to crawlers that don't execute JavaScript (Perplexity, ChatGPT,
// Claude, Copilot, and older Bingbot). It works for Googlebot, which does
// execute JS with a delay. The static bits in `public/index.html`
// (Organization + SoftwareApplication schemas, favicon, base meta) are what
// LLM crawlers actually see. Until we prerender the marketing routes, treat
// this hook as a Google-first optimization; the static file is the LLM
// surface.
//
// Prior version had a bug: `document.querySelector('script[type=...json"]')`
// matched the first JSON-LD block in index.html and clobbered it. All
// hook-managed scripts now use unique IDs so static schemas are untouched.

import { useEffect } from 'react';

const MANAGED_SCRIPT_IDS = {
  page: 'seo-webpage-jsonld',
  breadcrumb: 'seo-breadcrumb-jsonld',
  faq: 'seo-faq-jsonld',
  product: 'seo-product-jsonld',
};

function upsertMetaTag(name, content, { property = false } = {}) {
  if (!content) return;
  const attr = property ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

const useSEO = ({
  title,
  description,
  canonical,
  ogImage,
  // Item 24 — per-page robots meta control. Pass e.g. 'noindex,follow' to
  // hide a thank-you or utility page from search.
  robots,
  // Item 32 — content freshness signals.
  datePublished,
  dateModified,
  // Item 4 — page-level structured data. Pass arrays / objects to enable.
  breadcrumbs, // [{ name, url }, ...]
  faqs,        // [{ question, answer }, ...]
  productSchema, // full Product schema object
} = {}) => {
  useEffect(() => {
    if (title) document.title = title;

    upsertMetaTag('description', description);
    upsertMetaTag('robots', robots);

    upsertMetaTag('og:title', title, { property: true });
    upsertMetaTag('og:description', description, { property: true });
    upsertMetaTag('og:image', ogImage, { property: true });
    upsertMetaTag('og:url', canonical, { property: true });
    upsertMetaTag('og:type', 'website', { property: true });

    upsertMetaTag('twitter:card', 'summary_large_image');
    upsertMetaTag('twitter:title', title);
    upsertMetaTag('twitter:description', description);
    upsertMetaTag('twitter:image', ogImage);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
    }

    // Page-level WebPage schema. Freshness fields are only emitted when
    // provided by the caller — no fake defaults.
    const webPage = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description || 'AI-powered video dubbing platform with perfect lip-sync in 32 languages',
      url: canonical,
      ...(datePublished && { datePublished }),
      ...(dateModified && { dateModified }),
    };
    upsertJsonLd(MANAGED_SCRIPT_IDS.page, webPage);

    // Breadcrumb schema (item 4 + 8). Renders alongside the visual
    // breadcrumb component; both must match to satisfy Google's guidelines.
    upsertJsonLd(
      MANAGED_SCRIPT_IDS.breadcrumb,
      breadcrumbs && breadcrumbs.length
        ? {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbs.map((b, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: b.name,
              item: b.url,
            })),
          }
        : null
    );

    // FAQPage schema (item 4). Google renders these as expandable
    // rich-result rows directly in SERP; LLMs quote them near-verbatim.
    upsertJsonLd(
      MANAGED_SCRIPT_IDS.faq,
      faqs && faqs.length
        ? {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.question,
              acceptedAnswer: { '@type': 'Answer', text: f.answer },
            })),
          }
        : null
    );

    upsertJsonLd(MANAGED_SCRIPT_IDS.product, productSchema || null);
  }, [
    title,
    description,
    canonical,
    ogImage,
    robots,
    datePublished,
    dateModified,
    breadcrumbs,
    faqs,
    productSchema,
  ]);
};

export default useSEO;
